/**
 * Onboarding step 3, "Your school", revealed field by field: country (South
 * Africa first) → province (the nine SA provinces as big chips, free text
 * elsewhere) → city or town (suggestions + free text) → school (search, or add
 * a new one by typing its full name). Searches are debounced and never block
 * typing; if they fail, free text still works.
 */
import { useEffect, useId, useState } from 'react';
import type { Locale } from '@dropstack/core';
import type { BackendClient, SchoolOption } from '../../contracts.ts';
import { useT } from '../../i18n/index.ts';
import { provincesFor, sortedCountries } from '../../services/geo.ts';
import { foldText } from '../../services/text.ts';
import { ChoiceGroup } from '../../components/Controls.tsx';
import { Icon } from '../../components/Icon.tsx';
import { useDebouncedValue } from '../../components/hooks.ts';
import type { ProfileDraft } from '../model/onboarding.ts';

const SEARCH_DEBOUNCE_MS = 250;
const MAX_CITY_SUGGESTIONS = 6;
const MAX_SCHOOL_RESULTS = 6;

export interface SchoolStepProps {
  draft: ProfileDraft;
  update(patch: Partial<ProfileDraft>): void;
  backend: BackendClient | null;
  locale: Locale;
}

export function SchoolStep({ draft, update, backend, locale }: SchoolStepProps) {
  const t = useT();
  const countryId = useId();
  const provinceId = useId();
  const provinces = provincesFor(draft.country);
  const hasProvince = provinces.length === 0 || provinces.some((p) => p.code === draft.province);

  return (
    <div className="onboarding__step onboarding__school">
      <p className="onboarding__text">{t('shell.obSchoolText')}</p>

      <div className="onboarding__field">
        <label className="onboarding__label" htmlFor={countryId}>
          {t('shell.obCountry')}
        </label>
        <select
          id={countryId}
          className="ui-select"
          value={draft.country}
          onChange={(e) => update({ country: e.currentTarget.value, province: '', city: '', schoolId: null, schoolName: '' })}
        >
          {sortedCountries(locale).map((c) => (
            <option key={c.code} value={c.code}>
              {c.name[locale]}
            </option>
          ))}
        </select>
      </div>

      {provinces.length > 0 ? (
        <ChoiceGroup<string>
          legend={t('shell.obProvince')}
          variant="chips"
          value={draft.province || null}
          onChange={(province) => update({ province, city: '', schoolId: null, schoolName: '' })}
          choices={provinces.map((p) => ({ value: p.code, label: p.name[locale] }))}
        />
      ) : (
        <div className="onboarding__field">
          <label className="onboarding__label" htmlFor={provinceId}>
            {t('shell.obProvinceFree')}
          </label>
          <input
            id={provinceId}
            className="ui-input"
            value={draft.province}
            maxLength={60}
            autoComplete="off"
            onChange={(e) => update({ province: e.currentTarget.value })}
          />
        </div>
      )}

      {hasProvince && <CityField draft={draft} update={update} backend={backend} />}
      {hasProvince && draft.city.trim() !== '' && <SchoolField draft={draft} update={update} backend={backend} />}
    </div>
  );
}

function CityField({ draft, update, backend }: Omit<SchoolStepProps, 'locale'>) {
  const t = useT();
  const id = useId();
  const query = useDebouncedValue(draft.city, SEARCH_DEBOUNCE_MS);
  const [cities, setCities] = useState<string[]>([]);

  useEffect(() => {
    if (!backend) return;
    let live = true;
    backend.suggestCities(query, { country: draft.country, province: draft.province || undefined }).then(
      (list) => live && setCities(list.slice(0, MAX_CITY_SUGGESTIONS)),
      () => live && setCities([]),
    );
    return () => {
      live = false;
    };
  }, [backend, query, draft.country, draft.province]);

  // Hide the list once the typed city is exactly one of the suggestions.
  const exact = cities.some((c) => foldText(c) === foldText(draft.city));
  const shown = exact ? [] : cities;

  return (
    <div className="onboarding__field">
      <label className="onboarding__label" htmlFor={id}>
        {t('shell.obCity')}
      </label>
      <input
        id={id}
        className="ui-input"
        value={draft.city}
        maxLength={60}
        autoComplete="off"
        aria-describedby={`${id}-hint`}
        onChange={(e) => update({ city: e.currentTarget.value, schoolId: null })}
      />
      <p id={`${id}-hint`} className="onboarding__hint">
        {t('shell.obCityHint')}
      </p>
      {shown.length > 0 && (
        <ul className="onboarding__chips" aria-label={t('shell.obCitySuggestions')}>
          {shown.map((city) => (
            <li key={city}>
              <button type="button" className="ui-chip" onClick={() => update({ city, schoolId: null })}>
                <Icon name="pin" />
                {city}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SchoolField({ draft, update, backend }: Omit<SchoolStepProps, 'locale'>) {
  const t = useT();
  const id = useId();
  const query = useDebouncedValue(draft.schoolName, SEARCH_DEBOUNCE_MS);
  const [schools, setSchools] = useState<SchoolOption[]>([]);

  useEffect(() => {
    if (!backend) return;
    let live = true;
    const filter = { country: draft.country, province: draft.province || undefined, city: draft.city.trim() || undefined };
    backend.searchSchools(query, filter).then(
      (list) => live && setSchools(list.slice(0, MAX_SCHOOL_RESULTS)),
      () => live && setSchools([]),
    );
    return () => {
      live = false;
    };
  }, [backend, query, draft.country, draft.province, draft.city]);

  const typed = draft.schoolName.trim();
  const picked = draft.schoolId !== null;
  const exactMatch = schools.some((s) => foldText(s.name) === foldText(typed));

  return (
    <div className="onboarding__field">
      <label className="onboarding__label" htmlFor={id}>
        {t('shell.obSchool')}
      </label>
      <input
        id={id}
        className="ui-input"
        value={draft.schoolName}
        maxLength={80}
        autoComplete="off"
        aria-describedby={`${id}-hint`}
        onChange={(e) => update({ schoolName: e.currentTarget.value, schoolId: null })}
      />
      <p id={`${id}-hint`} className="onboarding__hint">
        {picked ? (
          <span className="onboarding__picked">
            <Icon name="check" />
            {t('shell.obSchoolPicked')}
          </span>
        ) : (
          t('shell.obSchoolHint')
        )}
      </p>
      {!picked && schools.length > 0 && (
        <ul className="onboarding__results" aria-label={t('shell.obSchoolResults')}>
          {schools.map((s) => (
            <li key={s.id}>
              <button type="button" className="onboarding__result" onClick={() => update({ schoolId: s.id, schoolName: s.name })}>
                <Icon name="school" />
                <span className="onboarding__result-name">{s.name}</span>
                <span className="onboarding__result-place">{s.city}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!picked && typed.length >= 2 && !exactMatch && (
        <p className="onboarding__add">
          <Icon name="plus" />
          <span>{t('shell.obSchoolAdd', { name: typed })}</span>
        </p>
      )}
    </div>
  );
}
